const WEBSOCKET_PROTOCOL_TOKEN = /^[!#$%&'*+\-.0-9A-Z^_`|a-z~]+$/;

/** Build routing-only metadata without changing the raw application identity. */
export function buildBridgeRoutingKey(rawUserId: string): string {
  return encodeURIComponent(rawUserId);
}

/** Validate the encoded key before using it as a WebSocket subprotocol. */
export function isValidBridgeRoutingProtocol(routingKey: string): boolean {
  return WEBSOCKET_PROTOCOL_TOKEN.test(routingKey);
}

/** Require exactly one protocol in the raw WebSocket handshake header. */
export function hasSingleBridgeRoutingProtocolHeader(
  protocolHeader: string | string[] | undefined,
): boolean {
  return (
    typeof protocolHeader === "string" &&
    protocolHeader.length > 0 &&
    !protocolHeader.includes(",")
  );
}

// An encoded IMS id is about 35 characters. This header is unauthenticated and
// can be kilobytes, and every logged event sits in the logger's 10k buffer.
const MAX_ROUTING_PROTOCOL_LENGTH = 128;

/**
 * Read the routing key for LOGGING ONLY. The value comes from the
 * unauthenticated upgrade header, so it is a hint and never an identity.
 * Returns undefined for absent, over-long, or malformed input.
 */
export function readUnverifiedRoutingKey(
  protocolHeader: string | string[] | undefined,
): string | undefined {
  if (!hasSingleBridgeRoutingProtocolHeader(protocolHeader)) {
    return undefined;
  }
  const raw = protocolHeader as string;
  if (raw.length > MAX_ROUTING_PROTOCOL_LENGTH) {
    return undefined;
  }
  // buildBridgeRoutingKey only ever produces a percent-encoded token (see
  // isValidBridgeRoutingProtocol), so anything else here isn't a routing key
  // this client could have sent -- binary junk or control characters that
  // happen to decode without throwing. Reject it before it reaches the log.
  if (!isValidBridgeRoutingProtocol(raw)) {
    return undefined;
  }
  try {
    // decodeURIComponent throws URIError on a lone or malformed percent escape,
    // and this value reaches us before anything filters it.
    return decodeURIComponent(raw);
  } catch {
    return undefined;
  }
}

/** Echo the sole protocol selected by the WebSocket server. */
export function selectBridgeRoutingProtocol(
  protocols: Set<string>,
): string | false {
  if (protocols.size !== 1) {
    return false;
  }
  return protocols.values().next().value ?? false;
}
